import { NgClass } from "@angular/common";
import { AfterViewInit, Component, computed, effect, inject, input, ChangeDetectionStrategy } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { Chart } from "chart.js";
import "chartjs-adapter-luxon";
import { ProgressSpinnerModule } from "@openng/optimus-ui/progressspinner";
import { createTimeoutSignal } from "../../utils/timeout.utils";
import { TimeoutComponent } from "../../components/timeout/timeout.component";
import { Router } from "@angular/router";
@Component({
  standalone: true,
  selector: "app-energy-loss-yearly-chart",
  imports: [NgClass, ProgressSpinnerModule, TimeoutComponent],
  templateUrl: "./energy-loss-yearly-chart.component.html",
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: "./energy-loss-yearly-chart.component.scss",
})
export class EnergyLossYearlyChartComponent implements AfterViewInit {
  public chartId = input<string>();
  public chartData = input<any>();

  public loading = computed(() => this.chartData() === null);
  public timeOut = createTimeoutSignal(this.loading);

  private translate = inject(TranslateService);
  private router = inject(Router);

  private lineChart!: any;

  public ngAfterViewInit() {
    this.initChart();
  }

  constructor() {
    effect(() => {
      const datasets = this.chartData();
      if (datasets && this.lineChart) {
        this.lineChart.data.datasets = datasets;

        this.lineChart.update();
        this.setLabels(this.translate.currentLang);
      }
    });
  }

  public navigateToDetail() {
    this.router.navigate(["dashboard/energyTool"]);
  }
  /**
   * Initializes the line chart.
   * Called when the component is initialized, or when the chart data changes.
   *
   * Sets up the chart with the given chart id
   * Subscribes to the language change event, and updates the labels when the language changes.
   */
  public initChart() {
    Chart.defaults.font.size = 17;
    const chartId = this.chartId();
    if (!chartId) {
      return;
    }
    this.lineChart = new Chart(chartId, {
      type: "line",
      data: {
        datasets: [],
      },
      options: {
        maintainAspectRatio: false,
        elements: {
          point: {
            radius: 0.5,
          },
        },
        plugins: {
          legend: {
            display: true,
            labels: {
              color: "#ffffff",
            },
          },
        },
        scales: {
          y: {
            title: {
              display: true,
              text: "",
            },
            border: {
              display: false,
            },
            grid: {
              color: "#727272",
            },
            ticks: {
              color: "#ffffff",
            },
          },
          x: {
            type: "timeseries",
            adapters: {
              date: {
                locale: "de-DE",
              },
            },
            time: {
              unit: "month",
              displayFormats: {
                month: "MMM",
              },
            },
            title: {
              display: true,
              text: "",
            },
            grid: {
              display: false,
              color: "#727272",
            },
            ticks: {
              maxTicksLimit: 12,
              color: "#ffffff",
              stepSize: 1,
            },
          },
        },
      },
    });

    this.translate.onLangChange.subscribe(() => {
      this.setLabels(this.translate.currentLang);
    });
  }

  /**
   * Translates and updates the labels and titles of the line chart based on the given locale.
   * @param lang The locale to use for the translation.
   */
  private setLabels(lang: string) {
    this.translate
      .get([
        "energy_loss_yearly_chart.energy_loss",
        "energy_loss_yearly_chart.current_year",
        "energy_loss_yearly_chart.line_label",
        "energy_loss_yearly_chart.expected_label",
      ])
      .subscribe((translations) => {
        if (this.lineChart.data.datasets.length >= 2) {
          this.lineChart.data.datasets[0].label = translations["energy_loss_yearly_chart.line_label"];
          this.lineChart.data.datasets[1].label = translations["energy_loss_yearly_chart.expected_label"];
          this.lineChart.options.scales.x.adapters.date.locale = lang;
          this.lineChart.options.scales.y.title.text = translations["energy_loss_yearly_chart.energy_loss"];
          this.lineChart.options.scales.x.title.text = translations["energy_loss_yearly_chart.current_year"];
          this.lineChart.update();
        }
      });
  }
}
